import { Link } from 'react-router-dom'
import { useWeeklyActivity } from '@/hooks/useWeeklyActivity'
import AndroidIcon from '@/components/common/AndroidIcon'
import LoadingSpinner from '@/components/common/LoadingSpinner'
import { formatDuration, formatDurationCompact } from '@/common/duration'

// 実行シナリオ数に応じたセルの配色(ヒートマップ)
function cellStyle(count: number): { backgroundColor: string; color: string } {
  if (!count) return { backgroundColor: '#f9fafb', color: '#9ca3af' } // gray-50 / gray-400
  if (count < 5) return { backgroundColor: '#dbeafe', color: '#1e3a8a' } // blue-100
  if (count < 15) return { backgroundColor: '#93c5fd', color: '#1e3a8a' } // blue-300
  if (count < 30) return { backgroundColor: '#3b82f6', color: '#ffffff' } // blue-500
  return { backgroundColor: '#1d4ed8', color: '#ffffff' } // blue-700
}

// YYYY-MM-DD を "M/D(曜)" 形式(JST)に整形
function formatDayHeader(date: string): string {
  const d = new Date(`${date}T00:00:00+09:00`)
  return d.toLocaleDateString('ja-JP', {
    timeZone: 'Asia/Tokyo',
    month: 'numeric',
    day: 'numeric',
    weekday: 'short',
  })
}

const NBSP = ' '

export default function WeeklyActivityCalendar() {
  // シミュレータ(sim-*)は除外する
  const { data, isLoading, isFetching } = useWeeklyActivity({
    show_sim: 'false',
  })

  const dates = data?.dates ?? []
  const rows = data?.data ?? []

  return (
    <div
      className={`bg-white rounded-lg shadow p-6 ${isFetching ? 'opacity-60' : ''}`}
    >
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">
            週次稼働状況（実行シナリオ数 / 実行時間）
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            直近7日間（当日を除く）。各セル上段=実行数、下段=実行時間
          </p>
        </div>
      </div>

      {isLoading ? (
        <div className="flex flex-col items-center justify-center h-48 gap-2">
          <LoadingSpinner size="lg" />
          <span className="text-gray-600">データを取得中...</span>
        </div>
      ) : rows.length === 0 ? (
        <div className="flex items-center justify-center h-48 text-gray-500">
          フォークリフトがありません
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full border-separate border-spacing-1">
            <thead>
              <tr>
                <th className="sticky left-0 z-10 w-px bg-white px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  フォークリフト / サイト
                </th>
                <th className="w-px px-6 py-2 text-center text-xs font-medium text-gray-500 whitespace-nowrap">
                  UIバージョン
                </th>
                {dates.map((date) => (
                  <th
                    key={date}
                    className="px-2 py-2 text-center text-xs font-medium text-gray-500 whitespace-nowrap"
                  >
                    {formatDayHeader(date)}
                  </th>
                ))}
                <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 whitespace-nowrap">
                  週間合計
                </th>
                <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 whitespace-nowrap">
                  総合計
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.fork_id}>
                  <td className="sticky left-0 z-10 bg-white px-3 py-2 whitespace-nowrap">
                    <Link
                      to={`/forklifts/${row.fork_id}`}
                      className="text-sm font-medium text-gray-900 hover:text-primary-600"
                    >
                      {row.fork_id}
                    </Link>
                    <span className="ml-2 text-xs text-gray-500">
                      {row.site_id}
                    </span>
                  </td>
                  <td
                    className="px-6 py-1 text-center whitespace-nowrap"
                    title="最後に使用されたUIのアプリバージョン"
                  >
                    <span className="text-xs text-gray-600">
                      {row.app_version ?? '—'}
                    </span>
                    {row.app_version_android && (
                      <div className="flex items-center justify-center gap-0.5 text-[10px] leading-tight text-gray-500">
                        <AndroidIcon className="w-3 h-3 flex-shrink-0" />
                        {row.app_version_android}
                      </div>
                    )}
                  </td>
                  {dates.map((date) => {
                    const count = row.counts[date] ?? 0
                    const durationMs = row.durations?.[date] ?? 0
                    // データなしの日はリンクにしない。上段=実行数 / 下段=実行時間 の2段でセル高さを揃える
                    if (!count) {
                      return (
                        <td key={date} className="p-0">
                          <div
                            className="px-2 py-1 text-center rounded"
                            style={cellStyle(0)}
                          >
                            <div className="text-sm font-medium">{NBSP}</div>
                            <div className="text-[10px] leading-tight">
                              {NBSP}
                            </div>
                          </div>
                        </td>
                      )
                    }
                    return (
                      <td key={date} className="p-0">
                        <Link
                          to={`/forklifts/${encodeURIComponent(row.fork_id)}?date=${date.replace(/-/g, '')}&start=0000&end=2359`}
                          className="block px-2 py-1 text-center rounded hover:ring-2 hover:ring-inset hover:ring-primary-500"
                          style={cellStyle(count)}
                          title={`${row.fork_id} / ${date}: ${count} 件 / ${formatDuration(durationMs)} — その日の詳細を表示`}
                        >
                          <div className="text-sm font-medium">{count}</div>
                          <div className="text-[10px] leading-tight opacity-90">
                            {formatDurationCompact(durationMs) || NBSP}
                          </div>
                        </Link>
                      </td>
                    )
                  })}
                  <td className="px-2 py-1 text-center">
                    <div className="text-sm font-semibold text-gray-900">
                      {row.total}
                    </div>
                    <div className="text-[10px] leading-tight text-gray-500">
                      {formatDurationCompact(row.total_duration_ms) || '—'}
                    </div>
                  </td>
                  <td className="px-2 py-1 text-center">
                    <div className="text-sm font-semibold text-gray-900">
                      {row.grand_total}
                    </div>
                    <div className="text-[10px] leading-tight text-gray-500">
                      {formatDurationCompact(row.grand_total_duration_ms) ||
                        '—'}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
